import React, { useEffect, useRef, useState } from 'react';
import { X, Search, Users, Info, Globe, Swords, Share as ShareIcon } from 'lucide-react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import html2canvas from 'html2canvas';
import { getWeekId } from '../utils/scoring';
import { getDeviceCountry, getTeamCountry, setTeamCountry, allCountryCodes, flagEmoji, countryName, perCapita, POPULATION_M } from '../utils/country';
import { shareImage } from '../utils/shareImage';

// Two tabs: PLAYERS (the original weekly per-user standings — your medal) and
// NATIONS (country vs. country — your team). Both stay; nothing removed.
const MIN_SESSIONS_TO_CHART = 1; // a country needs this many sessions to show. Kept
// at 1 while the aggregate is young (it only began with the 1.1 data model) so the
// tab reflects the flags users see on the players list; raise as volume grows.

const Leaderboard = ({ db, appId, currentUser, onClose }) => {
  const [tab, setTab] = useState('players');

  return (
    <div className="absolute inset-0 z-[2000] bg-black/90 flex items-center justify-center p-4 animate-in fade-in duration-300">
      <div className="w-full max-w-sm bg-slate-900 border border-slate-700 rounded-[2rem] p-5 shadow-2xl flex flex-col max-h-[85vh]">

        {/* Header */}
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-2">
            {tab === 'players' ? <Users className="text-emerald-500" size={20} /> : <Globe className="text-brand-teal" size={20} />}
            <h2 className="text-lg font-black italic text-white uppercase tracking-tighter leading-none">
              {tab === 'players' ? 'Weekly Leaderboard' : 'Bender Wars · Nations'}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors"><X size={24}/></button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-800/80 p-1 rounded-xl mb-4 border border-slate-700">
          <button onClick={() => setTab('players')} className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-colors ${tab === 'players' ? 'bg-emerald-500 text-black' : 'text-slate-400'}`}>
            Players
          </button>
          <button onClick={() => setTab('nations')} className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-colors ${tab === 'nations' ? 'bg-brand-teal text-black' : 'text-slate-400'}`}>
            Nations
          </button>
        </div>

        {tab === 'players'
          ? <PlayersTab db={db} appId={appId} currentUser={currentUser} />
          : <NationsTab db={db} appId={appId} />}
      </div>
    </div>
  );
};

// ============================================================================
// PLAYERS — unchanged behaviour, plus a flag next to each name.
// ============================================================================
const PlayersTab = ({ db, appId, currentUser }) => {
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const currentWeek = getWeekId();
    const usersRef = collection(db, 'artifacts', appId, 'public', 'data', 'users');
    const q = query(usersRef, where('weekId', '==', currentWeek));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMembers(list.sort((a, b) => (b.score || 0) - (a.score || 0)));
    });
    return () => unsub();
  }, [db, appId]);

  const filtered = members.filter(m => m.name?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <>
      <div className="bg-slate-800/80 rounded-xl p-3 mb-4 border border-slate-700 flex gap-2 items-start">
        <Info className="text-cyan-400 shrink-0 mt-0.5" size={14} />
        {/* 1.4.3: the legend must match calculateSmartScore — drinks never earn
            points. (The old legend advertising 50 pts/drink survived the score
            rewrite and sat one tap from the HUD.) */}
        <p className="text-[9px] font-bold text-slate-300 uppercase leading-relaxed">
          <span className="text-emerald-400">Score Formula:</span><br/>
          👣 Steps (1pt per 20, max 500)<br/>
          💧 Waters (1-5 = 25pts, 6+ = 5pts) · +100 for pacing 1 water per 5 drinks<br/>
          Drinks earn nothing — each one not covered by water costs 25pts
        </p>
      </div>

      <div className="relative mb-4">
        <input type="text" placeholder="Find friends..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-slate-800 border border-slate-700 rounded-xl py-2.5 px-9 text-[11px] font-bold text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-colors" />
        <Search size={14} className="absolute left-3 top-3 text-slate-500" />
      </div>

      <div className="space-y-2.5 overflow-y-auto no-scrollbar flex-1">
        {filtered.map((member, idx) => {
          const isMe = currentUser?.uid === member.id;
          return (
            <div key={member.id} className={`flex items-center justify-between p-3.5 rounded-xl border transition-colors ${isMe ? 'bg-blue-900/20 border-blue-500/50' : 'bg-slate-800/40 border-slate-700'}`}>
              <div className="flex items-center gap-3">
                <span className={`font-mono font-bold text-xs ${idx < 3 ? 'text-yellow-500' : 'text-slate-500'}`}>#{idx + 1}</span>
                <div>
                  <p className={`font-bold text-xs ${isMe ? 'text-blue-300' : 'text-white'}`}>
                    {member.countryCode && <span className="mr-1">{flagEmoji(member.countryCode)}</span>}
                    {member.name || 'Anonymous'} {isMe && '(You)'}
                  </p>
                  {/* 1.4.3: rows on a RANKED list show water + movement, never
                      alcohol counts — those stay on your own private stats. */}
                  <p className="text-[9px] text-slate-400 font-black uppercase tracking-tighter mt-0.5">
                    {member.waters || 0} 💧 • {member.steps || 0} 👣
                  </p>
                </div>
              </div>
              <span className="font-mono font-bold text-base text-emerald-400">{member.score || 0}</span>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center p-8 text-slate-600 text-xs font-bold">No active squad members found for this week.</div>
        )}
      </div>
    </>
  );
};

// ============================================================================
// NATIONS — country standings from the public countryStats aggregate.
// ============================================================================
const NationsTab = ({ db, appId }) => {
  const [period, setPeriod] = useState('week');   // week | all
  // 1.4.3: no 'drinks' metric — ranking countries by alcohol consumed is
  // exactly the "encourages excessive consumption" surface Apple rejected.
  const [metric, setMetric] = useState('sessions'); // sessions | distance
  const [perCap, setPerCap] = useState(false);
  const [rows, setRows] = useState([]);
  const [h2h, setH2h] = useState({ a: null, b: null });
  const [sharing, setSharing] = useState(false);
  const h2hRef = useRef(null);

  // Your TEAM (chosen flag; defaults to device country). Picker below lets a
  // traveling Canadian keep banking nights for Canada from a Copacabana bender.
  const [myCountry, setMyCountry] = useState(getTeamCountry());
  const [showPicker, setShowPicker] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');
  const pickTeam = (cc) => {
    setTeamCountry(cc);              // null = back to auto (device)
    setMyCountry(getTeamCountry());  // re-resolve (handles the auto case)
    setShowPicker(false);
    setTeamSearch('');
  };
  const mySessions = Number(localStorage.getItem('bender_my_sessions') || 0);

  useEffect(() => {
    const pid = period === 'week' ? getWeekId() : 'all';
    const q = query(collection(db, 'artifacts', appId, 'public', 'data', 'countryStats'), where('period', '==', pid));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(r => r.countryCode && (r.sessions || 0) >= MIN_SESSIONS_TO_CHART);
      setRows(list);
    }, (err) => console.warn('countryStats listen error', err));
    return () => unsub();
  }, [db, appId, period]);

  const valueOf = (r) => {
    const raw = metric === 'distance' ? (r.distance || 0) / 1000 : (r[metric] || 0);
    if (!perCap) return raw;
    const pc = perCapita(raw, r.countryCode);
    return pc == null ? -1 : pc;
  };
  const fmt = (v) => {
    if (v < 0) return '—';
    if (metric === 'distance') return `${v.toFixed(perCap ? 2 : 0)} km`;
    return perCap ? v.toFixed(1) : Math.round(v).toLocaleString();
  };
  const sorted = [...rows].sort((a, b) => valueOf(b) - valueOf(a));
  const mine = rows.find(r => r.countryCode === myCountry);
  const pick = (cc) => setH2h(s => s.a === cc ? { ...s, a: null } : s.b === cc ? { ...s, b: null } : !s.a ? { ...s, a: cc } : { ...s, b: cc });
  const A = rows.find(r => r.countryCode === h2h.a), B = rows.find(r => r.countryCode === h2h.b);

  const shareH2H = async () => {
    if (!h2hRef.current || sharing) return;
    setSharing(true);
    try {
      await new Promise(r => setTimeout(r, 100));
      const canvas = await html2canvas(h2hRef.current, { backgroundColor: '#000000', scale: 3, useCORS: true, logging: false });
      await shareImage(canvas.toDataURL('image/png'), 'benderquest-nations.png', 'Bender Wars · Nations');
    } catch (e) { console.error('H2H share failed', e); }
    finally { setSharing(false); }
  };

  return (
    <>
      {/* Controls */}
      <div className="flex gap-1.5 mb-3">
        {['week', 'all'].map(p => (
          <button key={p} onClick={() => setPeriod(p)} className={`px-2.5 py-1.5 text-[9px] font-black uppercase tracking-widest rounded-lg border ${period === p ? 'bg-white text-black border-white' : 'text-slate-400 border-slate-700'}`}>
            {p === 'week' ? 'This week' : 'All time'}
          </button>
        ))}
        <div className="flex-1" />
        {['sessions', 'distance'].map(m => (
          <button key={m} onClick={() => setMetric(m)} className={`px-2 py-1.5 text-[9px] font-black uppercase tracking-widest rounded-lg border ${metric === m ? 'bg-brand-teal text-black border-brand-teal' : 'text-slate-400 border-slate-700'}`}>
            {m === 'sessions' ? 'Nights' : 'km'}
          </button>
        ))}
      </div>
      <label className="flex items-center gap-2 mb-3 text-[9px] font-black uppercase tracking-widest text-slate-400 cursor-pointer select-none">
        <input type="checkbox" checked={perCap} onChange={e => setPerCap(e.target.checked)} className="accent-teal-400" />
        Per million people
      </label>

      {/* Your team */}
      <div className="bg-slate-800/60 border border-slate-700 rounded-xl px-3 py-2.5 mb-3 flex items-center justify-between">
        <button onClick={() => setShowPicker(v => !v)} className="text-[10px] font-bold text-slate-300 text-left">
          <span className="text-base mr-1">{flagEmoji(myCountry)}</span>
          You're on team <span className="text-white font-black">{countryName(myCountry)}</span>
          <span className="ml-2 text-[9px] font-black uppercase tracking-widest text-brand-teal underline underline-offset-2">Change</span>
        </button>
        <div className="text-right text-[9px] font-black uppercase tracking-widest text-brand-teal">
          {/* "X of Y" only when coherent: all-time view and the country aggregate
              has at least your count (it lags — it only began with the 1.1 data
              model, and your local count is lifetime while the board can be
              week-filtered). Otherwise just own your number. */}
          {/* `mine` is undefined until your country appears in the standings —
              must be checked before dereferencing it (0 >= 0 was passing the old
              guard on a fresh install and crashing the tab). */}
          {period === 'all' && mine && (mine.sessions || 0) >= mySessions
            ? `${mySessions} of ${mine.sessions} nights`
            : `${mySessions} ${mySessions === 1 ? 'night' : 'nights'} logged`}
        </div>
      </div>

      {/* Team picker — dropdown under the team row. Changing teams only affects
          FUTURE sessions; nights already banked stay with the old country. */}
      {showPicker && (
        <div className="bg-slate-900 border border-slate-700 rounded-xl mb-3 flex flex-col max-h-60">
          <input
            autoFocus
            value={teamSearch}
            onChange={e => setTeamSearch(e.target.value)}
            placeholder="Search country…"
            className="m-2 bg-black border border-slate-700 rounded-lg px-3 py-2 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-brand-teal"
          />
          <div className="overflow-y-auto no-scrollbar px-2 pb-2 space-y-0.5">
            <button onClick={() => pickTeam(null)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-[11px] font-bold text-slate-300 hover:bg-slate-800">
              <span className="text-base">{flagEmoji(getDeviceCountry())}</span>
              Auto — where my phone is ({countryName(getDeviceCountry())})
            </button>
            {allCountryCodes()
              .filter(cc => !teamSearch || countryName(cc).toLowerCase().includes(teamSearch.toLowerCase()) || cc.toLowerCase().startsWith(teamSearch.toLowerCase()))
              .sort((a, b) => countryName(a).localeCompare(countryName(b)))
              .map(cc => (
                <button key={cc} onClick={() => pickTeam(cc)} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-[11px] font-bold hover:bg-slate-800 ${cc === myCountry ? 'text-brand-teal' : 'text-slate-300'}`}>
                  <span className="text-base">{flagEmoji(cc)}</span>
                  {countryName(cc)}
                  {cc === myCountry && <span className="ml-auto">✓</span>}
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Standings */}
      <div className="space-y-2 overflow-y-auto no-scrollbar flex-1 min-h-[120px]">
        {sorted.map((r, idx) => {
          const chosen = h2h.a === r.countryCode || h2h.b === r.countryCode;
          return (
            <button key={r.id} onClick={() => pick(r.countryCode)}
              className={`w-full flex items-center justify-between p-3 rounded-xl border text-left transition-colors ${chosen ? 'bg-brand-pink/15 border-brand-pink/60' : r.countryCode === myCountry ? 'bg-teal-900/20 border-teal-500/40' : 'bg-slate-800/40 border-slate-700'}`}>
              <div className="flex items-center gap-3">
                <span className={`font-mono font-bold text-xs ${idx < 3 ? 'text-yellow-500' : 'text-slate-500'}`}>#{idx + 1}</span>
                <span className="text-xl leading-none">{flagEmoji(r.countryCode)}</span>
                <div>
                  <p className="font-bold text-xs text-white">{countryName(r.countryCode)}</p>
                  <p className="text-[9px] text-slate-400 font-black uppercase tracking-tighter mt-0.5">
                    {r.sessions || 0} nights • {Math.round((r.distance || 0) / 1000)} km
                  </p>
                </div>
              </div>
              <span className="font-mono font-bold text-base text-brand-teal tabular-nums">{fmt(valueOf(r))}</span>
            </button>
          );
        })}
        {sorted.length === 0 && (
          <div className="text-center p-6 text-slate-600 text-xs font-bold leading-relaxed">
            No nation has logged {MIN_SESSIONS_TO_CHART}+ nights {period === 'week' ? 'this week' : 'yet'}.<br/>
            <span className="text-slate-500">Finish a bender to put {countryName(myCountry)} on the board.</span>
          </div>
        )}
      </div>

      {/* Head-to-head */}
      <div className="mt-3 pt-3 border-t border-slate-700">
        {!(A && B) ? (
          <p className="text-center text-[9px] font-black uppercase tracking-widest text-slate-500 flex items-center justify-center gap-2">
            <Swords size={12} /> Tap two nations for a head-to-head
          </p>
        ) : (
          <>
            <div ref={h2hRef} className="bg-black rounded-xl p-4 border border-slate-700">
              <p className="text-center text-[8px] font-black uppercase tracking-[0.3em] text-brand-pink mb-2">Bender Wars</p>
              <div className="grid grid-cols-3 items-center text-center">
                <div><div className="text-3xl">{flagEmoji(A.countryCode)}</div><div className="text-[9px] font-black text-white uppercase mt-1">{countryName(A.countryCode)}</div></div>
                <div className="font-black italic text-white text-xl">VS</div>
                <div><div className="text-3xl">{flagEmoji(B.countryCode)}</div><div className="text-[9px] font-black text-white uppercase mt-1">{countryName(B.countryCode)}</div></div>
              </div>
              {/* 1.4.3: nights + movement only — no alcohol stats in a "war". */}
              {[['Nights', 'sessions', v => v], ['Km walked', 'distance', v => Math.round(v / 1000)]].map(([label, key, f]) => {
                const va = key ? f(A[key] || 0) : f(A), vb = key ? f(B[key] || 0) : f(B);
                const show = (v) => typeof v === 'number' && !Number.isInteger(v) ? v.toFixed(1) : v;
                return (
                  <div key={label} className="grid grid-cols-3 items-center text-center mt-2">
                    <span className={`font-mono font-bold text-sm tabular-nums ${va > vb ? 'text-brand-teal' : 'text-slate-400'}`}>{show(va)}</span>
                    <span className="text-[8px] font-black uppercase tracking-widest text-slate-500">{label}</span>
                    <span className={`font-mono font-bold text-sm tabular-nums ${vb > va ? 'text-brand-teal' : 'text-slate-400'}`}>{show(vb)}</span>
                  </div>
                );
              })}
              {POPULATION_M[A.countryCode] && POPULATION_M[B.countryCode] && (
                <p className="text-center text-[8px] font-bold text-slate-500 uppercase tracking-widest mt-3">
                  Per million: {(perCapita(A.sessions || 0, A.countryCode)).toFixed(2)} vs {(perCapita(B.sessions || 0, B.countryCode)).toFixed(2)} nights
                </p>
              )}
              <p className="text-center text-[8px] font-black uppercase tracking-[0.25em] text-slate-600 mt-3">Bender Quest · {period === 'week' ? 'This week' : 'All time'}</p>
            </div>
            <button onClick={shareH2H} disabled={sharing} className="w-full mt-2 bg-white text-black font-black text-[10px] py-2.5 rounded-xl uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
              <ShareIcon size={14} /> {sharing ? 'Sharing…' : 'Share the matchup'}
            </button>
          </>
        )}
      </div>
    </>
  );
};

export default Leaderboard;
